import { useState, useMemo } from "react";
import { toast } from "sonner";
import { useAsyncData } from "@/hooks/useAsyncData";
import { listLeads, updateLeadStatus, removeLead, LEAD_STATUSES } from "@/lib/adminRepository";
import { ADMIN } from "@/constants/testIds";
import { Download, Trash2, Search } from "lucide-react";

const STATUS_STYLE = {
  new: "bg-bronze text-cream",
  contacted: "bg-blue-600 text-white",
  followup: "bg-amber-500 text-white",
  closed: "bg-olive text-cream",
};

function toCsv(rows) {
  const cols = ["date", "time", "name", "email", "phone", "enquiryType", "sourcePage", "status", "message"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

export default function LeadsPage() {
  const { data: leads, loading, error, reload } = useAsyncData(listLeads, []);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return leads.filter(
      (l) =>
        (statusFilter === "all" || (l.status || "new") === statusFilter) &&
        (!t ||
          [l.name, l.email, l.phone, l.message, l.enquiryType]
            .some((v) => String(v || "").toLowerCase().includes(t)))
    );
  }, [leads, q, statusFilter]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${filtered.length}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} leads`);
  };

  const setStatus = async (id, status) => {
    await updateLeadStatus(id, status);
    reload();
  };
  const del = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this lead?")) return;
    await removeLead(id);
    toast.success("Lead deleted");
    reload();
  };

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-3xl text-ink">Leads <span className="text-olive text-lg">({leads.length})</span></h1>
        <button onClick={exportCsv} data-testid={ADMIN.leadExport} className="btn-secondary inline-flex items-center gap-2">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-bronze" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid={ADMIN.leadSearch}
            placeholder="Search name, email, message…"
            className="editorial-input !pl-9 !py-2 min-w-[260px]"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="editorial-input !py-2">
          <option value="all">All statuses</option>
          {LEAD_STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </div>

      {loading && <p className="text-olive">Loading…</p>}
      {error && <p className="text-red-700">Failed to load. <button className="underline" onClick={reload}>Retry</button></p>}

      {!loading && !error && (
        <div className="overflow-x-auto border border-bronze/20 bg-cream">
          <table className="w-full text-sm" data-testid={ADMIN.leadsTable}>
            <thead className="bg-beige/60 text-left text-[0.62rem] uppercase tracking-wider text-olive">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Name</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Enquiry</th>
                <th className="p-3">Source</th>
                <th className="p-3">Status</th>
                <th className="p-3">Message</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} className="border-t border-bronze/10 align-top" data-testid={`admin-lead-${l.id}`}>
                  <td className="p-3 whitespace-nowrap text-olive">{l.date}<br />{l.time}</td>
                  <td className="p-3 font-medium text-ink">{l.name}</td>
                  <td className="p-3"><a className="text-bronze" href={`mailto:${l.email}`}>{l.email}</a><br /><span className="text-olive">{l.phone}</span></td>
                  <td className="p-3">{l.enquiryType || "—"}</td>
                  <td className="p-3 text-olive">{l.sourcePage || "—"}</td>
                  <td className="p-3">
                    <select
                      value={l.status || "new"}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`text-[0.62rem] uppercase tracking-wider px-2 py-1 ${STATUS_STYLE[l.status || "new"] || ""}`}
                      data-testid={`admin-lead-status-${l.id}`}
                    >
                      {LEAD_STATUSES.map((s) => (<option key={s} value={s} className="bg-cream text-ink">{s}</option>))}
                    </select>
                  </td>
                  <td className="p-3 max-w-xs text-ink_soft/80">{l.message}</td>
                  <td className="p-3"><button onClick={() => del(l.id)} className="text-red-600 hover:text-red-800"><Trash2 size={15} /></button></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-olive">No leads match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
